<template>
  <div v-if="isVisible" :class="$style.report_section">
    <contents-box type="inner">
      <div :class="$style.report_row">
        <dl>
          <dt>
            <i class="fa-solid fa-file-powerpoint"></i>月別レポート
          </dt>
          <dd v-if="!isTeacherMode">
            <select
              v-model="selectedSchoolId">
              <option :value="0">---学校・企業を選択---</option>
              <option
                v-for="s in schools"
                :key="s.id"
                :value="s.id">{{ s.name }}</option>
            </select>
          </dd>
          <dd>
            <select
              v-model="selectedMonth">
              <option value="">---月を選択---</option>
              <option
                v-for="option in monthOptions"
                :key="option.value"
                :value="option.value">{{ option.label }}</option>
            </select>
          </dd>
          <dd v-if="!isTeacherMode && useLiveData">
            <select
              v-model="outputFormat"
              :class="$style.format_select">
              <option value="pdf">PDF</option>
              <option value="pptx">PPTX</option>
            </select>
            <span :class="$style.format_hint_wrap">
              <i class="fa-regular fa-circle-question" :class="$style.format_hint_icon"></i>
              <span :class="$style.format_hint_tooltip">事務局サイトでのみ選択可能なオプション</span>
            </span>
          </dd>
        </dl>
        <basic-btn
          tag="button"
          :disabled="!canDownload || downloading"
          @click="submitReport">
          <span v-if="downloading">ダウンロード中...</span>
          <span v-else>ダウンロード</span>
        </basic-btn>
        <label v-if="!isTeacherMode" :class="$style.test_mode">
          <input
            type="checkbox"
            v-model="useLiveData" />
          最新データで確認
        </label>
      </div>
    </contents-box>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import ContentsBox from '@/views/components/ContentsBox.vue';
import BasicBtn from '@/views/components/BasicBtn.vue';
import moment from 'moment';
import cf from '@/mixins/commonFunctions.js';
import { REPORT_SCHOOLS, getReportReleaseDate, REPORT_FEATURE_RELEASE_AT } from '@/constants/report';

export default {
  name: 'MonthlyReportDownload',
  mixins: [cf],
  components: {
    ContentsBox,
    BasicBtn,
  },
  props: {
    mode: {
      type: String,
      required: true,
      validator: (v) => ['operator', 'teacher'].includes(v),
    },
    school: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      selectedMonth: '',
      selectedSchoolId: 0,
      downloading: false,
      useLiveData: false,
      outputFormat: 'pdf',
      schools: [],
    };
  },
  created() {
    if (!this.isTeacherMode) {
      this.fetchSchools();
    }
  },
  computed: {
    ...mapState(['helper']),
    isTeacherMode() {
      return this.mode === 'teacher';
    },
    isReleaseGated() {
      // production / demo 環境では公開日（firstMonth + 1 月の 1 日）まで UI を非表示・選択肢を制限する
      // (school-report-spec.md L154-155 で demo は production と同じ挙動と仕様化されている)
      return ['production', 'demo'].includes(this.helper.env.name);
    },
    /**
     * operator のみ: 「最新データで確認」チェック ON の場合はライブ生成経路
     */
    useLivePath() {
      return !this.isTeacherMode && this.useLiveData;
    },
    /**
     * 現在の対象学校ID
     */
    currentSchoolId() {
      if (this.isTeacherMode) {
        return this.school ? this.school.id : null;
      }
      return this.selectedSchoolId;
    },
    /**
     * 対象学校の公開日（firstMonth の翌月1日）
     */
    releaseDate() {
      return getReportReleaseDate(this.currentSchoolId);
    },
    /**
     * リリースゲート対象環境（production / demo）の表示可否
     * - 機能全体公開日時 REPORT_FEATURE_RELEASE_AT 以降が前提
     * - operator: 機能ゲート通過のみで欄表示（学校 select が内側にあるため、未選択でも欄は出す）
     * - teacher : 機能ゲート通過 + 対象校の公開日（firstMonth + 1 月の 1 日）以降の両方を満たした場合
     * リリースゲート対象外環境では常に表示
     */
    isVisible() {
      if (!this.isReleaseGated) return true;
      if (!moment().isSameOrAfter(REPORT_FEATURE_RELEASE_AT)) return false;
      if (!this.isTeacherMode) return true;
      if (!this.releaseDate) return false;
      return moment().isSameOrAfter(this.releaseDate, 'day');
    },
    /**
     * 対象学校のレポート初月を取得
     * teacher モード: school prop から、operator モード: selectedSchoolId から
     */
    firstMonth() {
      const config = REPORT_SCHOOLS[this.currentSchoolId];
      if (config) return moment(config.firstMonth, 'YYYY-MM');
      // リリースゲート対象外: 未登録学校でも動作確認できるよう制限なし
      if (!this.isReleaseGated) return moment('2020-01', 'YYYY-MM');
      return null;
    },
    /**
     * 月選択肢を生成
     * - OFF (teacher または operator OFF): firstMonth 〜 前月を降順。確定 snapshot DL の対象
     * - ON (operator のみ): 当月「YYYY年M月以降」＋過去 4 ヶ月。ライブ DB 生成の対象
     */
    monthOptions() {
      // operator: 学校・企業が未選択なら月の選択肢を出さない
      if (!this.isTeacherMode && !this.selectedSchoolId) return [];
      if (!this.firstMonth) return [];

      if (this.useLivePath) {
        const options = [];
        const now = moment();

        if (now.isSameOrAfter(this.firstMonth, 'month')) {
          options.push({
            value: now.format('YYYY-MM'),
            label: `${now.format('YYYY年M月')}以降`,
          });
        }

        for (let i = 1; i <= 4; i += 1) {
          const m = moment().subtract(i, 'months');
          if (this.isReleaseGated && m.isBefore(this.firstMonth, 'month')) break;
          options.push({
            value: m.format('YYYY-MM'),
            label: m.format('YYYY年M月'),
          });
        }

        return options;
      }

      // OFF: firstMonth 〜 前月を降順。確定済み snapshot のみを対象とする。
      // 当月 snapshot は翌月初バッチ (毎月 1 日 00:00) 完了まで存在しないため、
      // 全環境で上限は「前月」に固定する (school-report-spec.md L142 / L158)。
      // 当月のテスト出力が必要な場合は ON (useLivePath = ライブ DB 生成) を使う。
      const upper = moment().utcOffset('+09:00').startOf('month').subtract(1, 'months');
      if (upper.isBefore(this.firstMonth, 'month')) return [];
      const options = [];
      const cursor = upper.clone();
      while (cursor.isSameOrAfter(this.firstMonth, 'month')) {
        options.push({
          value: cursor.format('YYYY-MM'),
          label: cursor.format('YYYY年M月'),
        });
        cursor.subtract(1, 'months');
      }
      return options;
    },
    authHeaders() {
      if (!this.isTeacherMode) return {};
      const ls = cf.getLocalStorage('wtt');
      if (ls && ls.jwt) {
        return { Authorization: `${ls.jwt.type} ${ls.jwt.token}` };
      }
      return {};
    },
    canDownload() {
      if (!this.selectedMonth) return false;
      if (!this.isTeacherMode && !this.selectedSchoolId) return false;
      return true;
    },
    /**
     * 新 API のベース URL（Teacher: JWT、Operator: API token）
     */
    baseApiUrl() {
      return this.isTeacherMode ? '/v1/teacher/reports' : '/v1/reports';
    },
  },
  watch: {
    useLiveData() {
      // 選択肢セットが入れ替わるので stale な selection をリセット
      this.selectedMonth = '';
    },
    selectedSchoolId() {
      this.selectedMonth = '';
    },
  },
  methods: {
    async fetchSchools() {
      try {
        const response = await this.axios({
          method: 'GET',
          url: '/v1/school/get/list',
          params: { isUsers: false },
        });
        const res = response.data.data;
        // production / demo では学校別公開日（firstMonth + 1 月の 1 日）に未到達の学校をドロップダウンから除外
        this.schools = res.data
          .filter((s) => s.id in REPORT_SCHOOLS)
          .filter((s) => {
            if (!this.isReleaseGated) return true;
            const rd = getReportReleaseDate(s.id);
            return rd && moment().isSameOrAfter(rd, 'day');
          });
      } catch (e) {
        console.log('学校リスト取得エラー:', e.message || e);
      }
    },
    /**
     * チェックボックスに応じて確定版 / ライブ版のどちらかを呼ぶ
     * - OFF: GET  /v1/[teacher/]reports/:period          (snapshot 必須、S3 キャッシュ)
     * - ON : POST /v1/[teacher/]reports/:period/preview  (ライブ DB 生成)
     */
    async submitReport() {
      if (!this.canDownload || this.downloading) return;
      this.downloading = true;
      try {
        const fmt = this.outputFormat;
        const live = this.useLivePath;
        const response = await this.axios({
          method: live ? 'POST' : 'GET',
          url: live
            ? `${this.baseApiUrl}/${this.selectedMonth}/preview`
            : `${this.baseApiUrl}/${this.selectedMonth}`,
          headers: this.authHeaders,
          params: this.buildApiParams(fmt),
          responseType: 'blob',
          timeout: fmt === 'pdf' ? 90000 : 30000,
        });
        this.saveAsDownload(response.data, fmt);
      } catch (e) {
        await this.handleReportError(e);
      } finally {
        this.downloading = false;
      }
    },

    /**
     * API クエリパラメータを組み立てる
     * - format: pdf/pptx（必須）
     * - school_id: operator のみ付与（teacher は JWT から取得）
     */
    buildApiParams(format) {
      const params = { format };
      if (!this.isTeacherMode) {
        params.school_id = this.selectedSchoolId;
      }
      return params;
    },

    /**
     * blob レスポンスをダウンロードとして保存
     */
    saveAsDownload(data, fmt) {
      const isPdf = fmt === 'pdf';
      const mimeType = isPdf
        ? 'application/pdf'
        : 'application/vnd.openxmlformats-officedocument.presentationml.presentation';
      const ext = isPdf ? 'pdf' : 'pptx';
      const blob = new Blob([data], { type: mimeType });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `report_${this.currentSchoolId}_${this.selectedMonth}.${ext}`;
      link.click();
      window.URL.revokeObjectURL(url);
    },

    /**
     * エラー応答のハンドリング
     * - 422 snapshot_not_found: スナップショット未確定のユーザ向けメッセージ
     * - それ以外: 汎用エラーメッセージ
     * responseType: 'blob' で取得しているため、422 も blob で返る → text 化して JSON パースする
     */
    async handleReportError(error) {
      if (error.response && error.response.status === 422 && error.response.data) {
        const payload = await this.readBlobAsJson(error.response.data);
        if (payload && payload.error === 'snapshot_not_found') {
          // `missing_months` は内部情報なのでユーザには見せない
          alert('対象月のデータはまだ確定していません。翌月1日以降に再度お試しください。');
          return;
        }
      }
      alert('レポートのダウンロードに失敗しました。');
    },

    /**
     * axios の responseType: 'blob' で返ってきたエラーボディを JSON として解釈する
     */
    async readBlobAsJson(blob) {
      if (!blob || typeof blob.text !== 'function') return null;
      try {
        const text = await blob.text();
        return JSON.parse(text);
      } catch (e) {
        return null;
      }
    },
  },
};
</script>

<style lang="scss" module>
.report_section {
  margin-top: 20px;
}
.report_row {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  dl {
    display: flex;
    align-items: center;
    margin: 0;
  }
  dt {
    white-space: nowrap;
    i {
      margin-right: .5em;
    }
  }
  dd {
    display: flex;
    align-items: center;
    margin-left: 1em;
    select {
      min-width: 200px;
      padding: 13px;
      border: none;
      border-radius: 8px;
      background-color: var(--gray-sub);
      font-size: 14px;
    }
  }
.format_select {
  min-width: 80px;
  padding: 13px;
  border: none;
  border-radius: 8px;
  background-color: var(--gray-sub);
  font-size: 14px;
}
.format_hint_wrap {
  position: relative;
  display: inline-flex;
  align-items: center;
  margin-left: 6px;
  cursor: help;
  &:hover .format_hint_tooltip {
    visibility: visible;
    opacity: 1;
  }
}
.format_hint_icon {
  font-size: 14px;
  color: #888;
}
.format_hint_tooltip {
  visibility: hidden;
  opacity: 0;
  position: absolute;
  bottom: calc(100% + 8px);
  left: 50%;
  transform: translateX(-50%);
  padding: 8px 12px;
  font-size: 12px;
  font-weight: normal;
  color: #fff;
  background-color: #333;
  border-radius: 6px;
  white-space: nowrap;
  transition: opacity 0.2s;
  z-index: 10;
  &::after {
    content: '';
    position: absolute;
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    border: 6px solid transparent;
    border-top-color: #333;
  }
}
.test_mode {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 13px;
  cursor: pointer;
  user-select: none;
  input {
    cursor: pointer;
  }
}
}
</style>
